import { LoadingButton } from '@mui/lab'
import { Alert, Collapse, LinearProgress } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import 'react-mde/lib/styles/css/react-mde-all.css'
import Link from '../../components/common/util/input/Link'
import { DropAlert } from '../../components/drops/DropAlert'
import { DropButton } from '../../components/drops/DropButton'
import { DropDetailsEditor } from '../../components/drops/DropDetailsEditor'
import {
    getCurrencies,
    dropStartDateValid,
} from '../../components/editor/DropCreatorUtils'
import {
    formatCurrency,
    getImage,
    sleep,
} from '../../components/helpers/FormatLinks'
import Page from '../../components/page/Page'
import { useSharedState } from '../../components/waxplorer/Waxplorer'
import { AirdropAmountEditor } from '../../components/airdrops/AirdropAmountEditor'
import { PreviewImageUpload } from '../../components/drops/pfpeditor/PreviewImageUpload'
import { AirdropSnapshotEditor } from '../../components/airdrops/AirdropSnapshotEditor'
import config from '../../config.json'
import { getBoostAction } from '../../components/helpers/WaxApi'
import { post } from '../../components/helpers/Api'

const DropCreatorPage = (props) => {
    const [state] = useSharedState()

    const activeUser = state?.activeUser

    const userName = activeUser?.accountName
        ? state?.activeUser.accountName
        : null

    const [previewImage, setPreviewImage] = useState(null)
    const [airdropCurrency, setAirdropCurrency] = useState({
        currency: '8,WAX,WAX',
        price: 0,
        contract: 'eosio.token',
    })
    const [holderCurrencyMin, setHolderCurrencyMin] = useState({
        currency: '4,WUF,WUF',
        price: 1,
        contract: 'wuffi',
    })
    const [holderCurrencyMax, setHolderCurrencyMax] = useState({
        currency: '4,WUF,WUF',
        price: 0,
        contract: 'wuffi',
    })

    const [fees, setFees] = useState('50000000.0000 WUF')

    const [dropName, setDropName] = useState('')
    const [dropDescription, setDropDescription] = useState('')
    const [snapshotDate, setSnapshotDate] = useState(new Date())
    const [currencies, setCurrencies] = useState([])
    const [isCreating, setIsCreating] = useState(null)
    const [error, setError] = useState(null)

    const { t } = useTranslation('common')

    const [editorOpen, setEditorOpen] = useState(false)

    const [createSuccess, setCreateSuccess] = useState(undefined)
    const [transactionId, setTransactionId] = useState(undefined)

    useEffect(() => {
        getCurrencies(state).then((res) => setCurrencies(res))
    }, [userName])

    const handleCreateDrop = async () => {
        setIsCreating(true)
        setError(null)

        const tokenSplit = airdropCurrency.currency.split(',')
        const tokenDecimals = parseInt(tokenSplit[0])
        const holderSplit = holderCurrencyMin.currency.split(',')
        const holderDecimals = parseInt(holderSplit[0])
        const transferAmount = formatCurrency(
            airdropCurrency.price,
            tokenDecimals,
            false,
            tokenSplit[1],
        )

        const data = {
            token: transferAmount,
            contract: airdropCurrency.contract,
            creator: userName,
            holder_contract: holderCurrencyMin.contract,
            min_amount: formatCurrency(
                holderCurrencyMin.price,
                holderDecimals,
                false,
                holderSplit[1],
            ),
            max_amount: formatCurrency(
                holderCurrencyMax.price,
                holderDecimals,
                false,
                holderSplit[1],
            ),
            snapshot_time: Math.floor(snapshotDate.getTime() / 1000),
            display_data: JSON.stringify({
                name: dropName,
                description: dropDescription,
                preview_img: previewImage,
            }),
        }

        const createDropActions = [getBoostAction(activeUser)]

        const transferAction = {
            account: airdropCurrency.contract,
            name: 'transfer',
            authorization: [
                {
                    actor: userName,
                    permission: activeUser.requestPermission
                        ? activeUser.requestPermission
                        : 'active',
                },
            ],
            data: {
                from: userName,
                to: 'wufclaimtool',
                quantity: transferAmount,
                memo: 'deposit',
            },
        }

        const transferAction2 = {
            account: 'wuffi',
            name: 'transfer',
            authorization: [
                {
                    actor: userName,
                    permission: activeUser.requestPermission
                        ? activeUser.requestPermission
                        : 'active',
                },
            ],
            data: {
                from: userName,
                to: 'wufclaimtool',
                quantity: fees ? fees : '50000000.0000 WUF',
                memo: 'deposit',
            },
        }

        const createDropAction = {
            account: 'wufclaimtool',
            name: 'addairdrop',
            authorization: [
                {
                    actor: userName,
                    permission: activeUser.requestPermission
                        ? activeUser.requestPermission
                        : 'active',
                },
            ],
            data: data,
        }

        createDropActions.push(transferAction)
        createDropActions.push(transferAction2)
        createDropActions.push(createDropAction)

        let createDropTransactionId = null

        try {
            const createDropResult = await activeUser.session.transact(
                {
                    actions: createDropActions,
                },
                {
                    expireSeconds: 300,
                    blocksBehind: 0,
                },
            )

            createDropTransactionId = createDropResult?.response?.transaction_id
            if (createDropTransactionId) {
                setCreateSuccess(true)
            }
            setTransactionId(createDropTransactionId)
        } catch (e) {
            setError(e.message)
        }

        setIsCreating(false)
    }

    const [fetchingDropId, setFetchingDropId] = useState(false)
    const [airdropId, setAirdropId] = useState(null)
    const fetchDropId = async (transactionId) => {
        setFetchingDropId(true)
        let actions = []
        let tries = 0
        while (actions.length === 0 && tries < 5) {
            await sleep(tries * 1000)
            const response = await fetch(
                (process.env.NEXT_PUBLIC_TESTNET === 'TRUE'
                    ? config.hyperiontest
                    : config.hyperion) +
                    `/v2/history/get_transaction?id=${transactionId}`,
            )
            const json = await response.json()
            actions = json.actions !== undefined ? json.actions : []
            tries++
        }
        for (const action of actions) {
            if (action.act.name === 'lognewdrop') {
                setAirdropId(action.act.data.airdrop_id)
                break
            }
        }
        setFetchingDropId(false)
    }

    const getFees = async () => {
        const body = {
            code: 'wufclaimtool',
            index_position: 1,
            json: 'true',
            key_type: 'i64',
            limit: 1,
            lower_bound: '',
            upper_bound: '',
            reverse: 'true',
            scope: 'wufclaimtool',
            show_payer: 'false',
            table: 'config',
            table_key: '',
        }

        const url =
            process.env.NEXT_PUBLIC_TESTNET === 'TRUE'
                ? config.testapi + '/v1/chain/get_table_rows'
                : state.api + '/v1/chain/get_table_rows'
        const res = await post(url, body)

        if (
            res &&
            res.status === 200 &&
            res.data &&
            res.data.rows &&
            res.data.rows.length > 0
        ) {
            return res.data.rows[0]['default_fee']
        } else {
            return false
        }
    }

    useEffect(() => {
        if (transactionId) {
            fetchDropId(transactionId)
        }
    }, [transactionId])

    useEffect(() => {
        getFees().then(setFees)
    }, [fees])

    const image = getImage('')

    return (
        <Page
            id="AirdropCreatorNewPage"
            image={image}
            banners={false}
            {...props}
            pageTitle={t('airdrops.create_airdrop')}
            breadcrumbs={[
                {
                    title: 'Home',
                    link: '/',
                },
                {
                    title: 'Airdrops',
                    link: '/airdrops',
                },
                {
                    title: 'New Airdrop',
                    link: '/airdrops/new',
                },
            ]}
        >
            <div className="mx-2 md:mx-5">
                {!userName ? (
                    <div className="my-10">
                        <Alert severity="error">
                            {t('cpu.please_log_in_to_continue')}
                        </Alert>
                    </div>
                ) : (
                    <>
                        <div>
                            <AirdropAmountEditor
                                airdropCurrency={airdropCurrency}
                                setAirdropCurrency={setAirdropCurrency}
                                holderCurrencyMin={holderCurrencyMin}
                                holderCurrencyMax={holderCurrencyMax}
                                setHolderCurrencyMin={setHolderCurrencyMin}
                                setHolderCurrencyMax={setHolderCurrencyMax}
                                currencies={currencies}
                                fees={fees}
                            />
                            <PreviewImageUpload
                                previewImage={previewImage}
                                setPreviewImage={setPreviewImage}
                                enablePreviewVideo={false}
                                explanation={t(
                                    'airdrops.preview_image_explanation',
                                )}
                            />
                            <DropDetailsEditor
                                dropName={dropName}
                                setDropName={setDropName}
                                editorOpen={editorOpen}
                                setEditorOpen={setEditorOpen}
                                dropDescription={dropDescription}
                                setDropDescription={setDropDescription}
                                title={t('airdrops.airdrop_details')}
                                nameTitle={t('airdrops.airdrop_name')}
                                descriptionTitle={t(
                                    'airdrops.airdrop_description',
                                )}
                            />
                            <AirdropSnapshotEditor
                                snapshotDate={snapshotDate}
                                setSnapshotDate={setSnapshotDate}
                            />

                            <DropAlert
                                condition={
                                    !holderCurrencyMin?.price ||
                                    (holderCurrencyMin.price >
                                        holderCurrencyMax.price >
                                        0 &&
                                        parseInt(holderCurrencyMax.price) !== 0)
                                }
                                severity="warning"
                                elementId="DropAmountEditor"
                            >
                                {t('drops.strongly_recommend_description')}
                            </DropAlert>

                            <DropAlert
                                condition={!editorOpen && !dropDescription}
                                severity="warning"
                                elementId="DropDetailsEditor"
                            >
                                {t('drops.strongly_recommend_description')}
                            </DropAlert>

                            <DropAlert
                                condition={dropName.length === 0}
                                severity="error"
                                elementId="DropDetailsEditor"
                            >
                                {t('airdrops.airdrop_name_is_missing')}
                            </DropAlert>

                            <DropAlert
                                condition={
                                    !dropStartDateValid(true, snapshotDate)
                                }
                                severity="error"
                                elementId="AirDropStartEndDateEditor"
                            >
                                {t('airdrops.snapshot_date_invalid')}
                            </DropAlert>

                            <Collapse in={!createSuccess}>
                                <div className="flex flex-wrap justify-center mt-5 mb-10">
                                    <DropButton
                                        onClick={handleCreateDrop}
                                        className="sm:mx-2 mb-5 sm:mb-0 w-full sm:w-52"
                                        fullWidth
                                        size="large"
                                        disabled={
                                            createSuccess ||
                                            dropName.length === 0 ||
                                            !dropStartDateValid(
                                                true,
                                                snapshotDate,
                                            ) ||
                                            !holderCurrencyMin?.price ||
                                            (holderCurrencyMin.price >
                                                holderCurrencyMax.price >
                                                0 &&
                                                parseInt(
                                                    holderCurrencyMax.price,
                                                ) !== 0) ||
                                            isCreating
                                        }
                                    >
                                        {t('airdrops.create_airdrop')}
                                    </DropButton>
                                    <DropButton
                                        className="sm:mx-2 mb-5 sm:mb-0 w-full sm:w-52"
                                        fullWidth
                                        href={'/airdrops'}
                                        size="large"
                                        color="error"
                                        disabled={isCreating}
                                    >
                                        {t('buttons.back_to_overview')}
                                    </DropButton>
                                </div>
                            </Collapse>

                            {isCreating ? <LinearProgress /> : ''}
                            {error ? (
                                <DropAlert
                                    condition={error}
                                    severity={'error'}
                                    closable={true}
                                    onClose={() => setError(false)}
                                >
                                    {error}
                                </DropAlert>
                            ) : (
                                ''
                            )}

                            <Collapse in={createSuccess && transactionId}>
                                <div className="flex justify-center">
                                    <div className="w-full lg:w-2/3">
                                        <div className="flex justify-evenly w-full mb-10">
                                            <DropButton
                                                className="w-1/3 px-1"
                                                variant="outlined"
                                                size="small"
                                                fullWidth
                                                color="error"
                                                href={'/airdrops'}
                                            >
                                                {t('buttons.back_to_overview')}
                                            </DropButton>
                                            <DropButton
                                                className="w-1/3 px-1"
                                                href={
                                                    (process.env
                                                        .NEXT_PUBLIC_TESTNET ===
                                                    'TRUE'
                                                        ? 'https://testnet.waxblock.io/transaction/'
                                                        : 'https://waxblock.io/transaction/') +
                                                    transactionId
                                                }
                                                external
                                                size="small"
                                                fullWidth
                                            >
                                                {t('drops.view_on_waxblock')}
                                            </DropButton>
                                            <div className="w-1/3 px-1">
                                                <LoadingButton
                                                    fullWidth
                                                    variant="outlined"
                                                    size="small"
                                                    disabled={fetchingDropId}
                                                    loading={fetchingDropId}
                                                >
                                                    <Link
                                                        href={`/airdrop/${airdropId}`}
                                                    >
                                                        {t(
                                                            'airdrops.view_airdrop',
                                                        )}
                                                    </Link>
                                                </LoadingButton>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Collapse>
                        </div>
                    </>
                )}
            </div>
        </Page>
    )
}

export default DropCreatorPage
